import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import { Sun, Wrench, CheckCircle2, Plus, X, History, Zap } from "lucide-react";

const KINDS = [
  { key: "solar", label: "Solar Packages", icon: Sun },
  { key: "maintenance", label: "Maintenance Plans", icon: Wrench },
];

export default function AdminPackages() {
  const [kind, setKind] = useState("solar");
  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(null); // package_id | "new"

  const load = () => {
    api.get(`/packages/${kind}/versions`).then((r) => setRows(r.data)).catch(() => toast.error("Load failed"));
  };
  useEffect(() => { load(); }, [kind]); // eslint-disable-line

  // Group versions by package_id
  const groups = {};
  rows.forEach((r) => {
    if (!groups[r.package_id]) groups[r.package_id] = [];
    groups[r.package_id].push(r);
  });

  const activate = async (vid) => {
    if (!confirm("Activate this version? The current one will be deactivated.")) return;
    try {
      await api.post(`/packages/versions/${vid}/activate`);
      toast.success("Activated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong — please try again."); }
  };

  const del = async (vid) => {
    if (!confirm("Delete this version?")) return;
    try {
      await api.delete(`/packages/versions/${vid}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong — please try again."); }
  };

  return (
    <div data-testid="admin-packages">
      <p className="overline">Sales · Pricing</p>
      <div className="flex items-center justify-between mb-6 mt-2">
        <h1 className="text-3xl font-heading font-medium text-[#0A2540] dark:text-white">Package Versions</h1>
      </div>

      <div className="flex gap-2 mb-6">
        {KINDS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setKind(key)}
            data-testid={`tab-${key}`}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm transition ${
              kind === key ? "bg-[#0A2540] text-white" : "bg-slate-100 dark:bg-slate-800 text-[#475569]"
            }`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      <div className="space-y-6">
        {Object.entries(groups).map(([pid, versions]) => (
          <div key={pid} className="card-pb overflow-hidden" data-testid={`group-${pid}`}>
            <div className="px-5 py-3 bg-slate-50 dark:bg-slate-800 border-b border-[#E2E8F0] dark:border-slate-700 flex items-center justify-between">
              <div className="font-medium text-[#0A2540] dark:text-white">
                {versions[0]?.spec?.name || pid}
                <span className="ml-2 text-[10px] font-mono text-[#94A3B8]">{pid}</span>
              </div>
              <button
                onClick={() => setShowForm({ package_id: pid, name: versions[0]?.spec?.name })}
                data-testid={`new-version-${pid}`}
                className="text-xs text-[#C9A356] hover:underline inline-flex items-center gap-1"
              >
                <Plus className="w-3.5 h-3.5" /> New version
              </button>
            </div>
            <ul className="divide-y divide-[#E2E8F0] dark:divide-slate-700">
              {versions.map((v) => (
                <li key={v.version_id} className="px-5 py-3 flex items-center justify-between" data-testid={`version-${v.version_id}`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 rounded-md grid place-items-center ${v.is_active ? "bg-emerald-100 text-emerald-600" : "bg-slate-100 dark:bg-slate-700 text-[#475569]"}`}>
                      {v.is_active ? <CheckCircle2 className="w-4 h-4" /> : <History className="w-4 h-4" />}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-[#0A2540] dark:text-white">
                        v{v.version}
                        {v.is_active && <span className="ml-2 text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">Active</span>}
                      </div>
                      <div className="text-xs text-[#475569] dark:text-slate-400">
                        ${v.spec?.price?.toLocaleString() || "—"}
                        {v.spec?.system_size_kw && ` · ${v.spec.system_size_kw} kW`}
                        {v.spec?.visits_per_year && ` · ${v.spec.visits_per_year} visits/yr`}
                      </div>
                      <div className="text-[10px] text-[#94A3B8] mt-0.5">
                        Created {v.created_at ? new Date(v.created_at).toLocaleDateString() : ""}
                        {v.activated_at && ` · Activated ${new Date(v.activated_at).toLocaleDateString()}`}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {!v.is_active && (
                      <>
                        <button onClick={() => activate(v.version_id)} data-testid={`activate-${v.version_id}`}
                                className="text-xs text-[#C9A356] hover:text-[#8A6B14] inline-flex items-center gap-1 px-2 py-1">
                          <Zap className="w-3.5 h-3.5" /> Activate
                        </button>
                        <button onClick={() => del(v.version_id)} className="text-xs text-red-600 hover:text-red-700 px-2 py-1">
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))}
        {Object.keys(groups).length === 0 && (
          <p className="text-sm text-[#94A3B8] italic py-8 text-center">No package versions yet.</p>
        )}
      </div>

      {showForm && (
        <VersionForm
          kind={kind}
          packageId={showForm.package_id}
          currentName={showForm.name}
          onClose={() => setShowForm(null)}
          onSaved={() => { setShowForm(null); load(); }}
        />
      )}
    </div>
  );
}

function VersionForm({ kind, packageId, currentName, onClose, onSaved }) {
  const [spec, setSpec] = useState({
    name: currentName || "",
    tagline: "",
    price: "",
    system_size_kw: "",
    panels: "",
    monthly_from: "",
    monthly_savings: "",
    warranty_years: "",
    install_days: "",
    visits_per_year: "",
    response_hours: "",
  });
  const [activate, setActivate] = useState(true);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!spec.name || !spec.price) { toast.error("Name and price required"); return; }
    setSaving(true);
    try {
      const payload = { spec: {}, activate };
      Object.entries(spec).forEach(([k, v]) => { if (v !== "" && v != null) payload.spec[k] = v; });
      await api.post(`/packages/${kind}/${packageId}/version`, payload);
      toast.success("Version created");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong — please try again."); }
    finally { setSaving(false); }
  };

  const set = (k, v) => setSpec({ ...spec, [k]: v });
  const cls = "w-full border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-2 text-sm";

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={onClose} role="dialog" aria-modal="true">
      <div className="bg-white dark:bg-slate-900 rounded-xl p-6 max-w-lg w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="version-form">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-xl text-[#0A2540] dark:text-white">New version for {packageId}</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <F label="Name *"><input value={spec.name} onChange={(e) => set("name", e.target.value)} className={cls} data-testid="v-name" /></F>
            <F label="Tagline"><input value={spec.tagline} onChange={(e) => set("tagline", e.target.value)} className={cls} /></F>
            <F label="Price ($) *"><input type="number" step="0.01" value={spec.price} onChange={(e) => set("price", e.target.value)} className={cls} data-testid="v-price" /></F>
            {kind === "solar" && (
              <>
                <F label="System size (kW)"><input type="number" step="0.1" value={spec.system_size_kw} onChange={(e) => set("system_size_kw", e.target.value)} className={cls} /></F>
                <F label="Panels"><input type="number" value={spec.panels} onChange={(e) => set("panels", e.target.value)} className={cls} /></F>
                <F label="Monthly savings ($)"><input type="number" value={spec.monthly_savings} onChange={(e) => set("monthly_savings", e.target.value)} className={cls} /></F>
                <F label="Warranty (years)"><input type="number" value={spec.warranty_years} onChange={(e) => set("warranty_years", e.target.value)} className={cls} /></F>
                <F label="Install days"><input type="number" value={spec.install_days} onChange={(e) => set("install_days", e.target.value)} className={cls} /></F>
              </>
            )}
            {kind === "maintenance" && (
              <>
                <F label="Visits / year"><input type="number" value={spec.visits_per_year} onChange={(e) => set("visits_per_year", e.target.value)} className={cls} /></F>
                <F label="Response (hours)"><input type="number" value={spec.response_hours} onChange={(e) => set("response_hours", e.target.value)} className={cls} /></F>
              </>
            )}
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-[#0A2540] dark:text-white">
            <input type="checkbox" checked={activate} onChange={(e) => setActivate(e.target.checked)} data-testid="v-activate" />
            Activate this version now (customers see it immediately)
          </label>
        </div>
        <div className="flex gap-2 justify-end mt-5">
          <button onClick={onClose} className="btn-outline-navy text-sm">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-accent text-sm" data-testid="save-version">
            {saving ? "Saving…" : "Create version"}
          </button>
        </div>
      </div>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-wider text-[#475569] dark:text-slate-400 mb-1 font-bold">{label}</label>
      {children}
    </div>
  );
}
